import {
  AnimatePresence,
  motion,
  type PanInfo,
  useDragControls,
  useReducedMotion,
} from 'framer-motion'
import { X } from 'lucide-react'
import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react'
import {
  iosBackdropTransition,
  iosSheetSpring,
  isNativeIOS,
  webSurfaceTransition,
} from '../../lib/iosMotion'
import { haptic } from '../../lib/native'
import { useUiText } from '../../i18n/ui'
import {
  useOverlayFocusReveal,
  useOverlayScrollLock,
} from '../../hooks/useOverlayBehavior'

export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  size = 'compact',
  hideTitle = false,
  className,
  headerContent,
  placement = 'center',
  showCloseButton = true,
}: PropsWithChildren<{
  open: boolean
  onClose: () => void
  title: string
  eyebrow?: ReactNode
  size?: 'compact' | 'wide'
  hideTitle?: boolean
  className?: string
  headerContent?: ReactNode
  placement?: 'center' | 'sheet'
  showCloseButton?: boolean
}>) {
  const { text } = useUiText()
  const headingId = useId()
  const dragControls = useDragControls()
  const reduceMotion = useReducedMotion()
  const nativeIOS = isNativeIOS()
  const supportsSheetDrag = nativeIOS && placement === 'sheet'
  const isSheet = placement === 'sheet'
  const panelRef = useRef<HTMLElement>(null)

  useOverlayScrollLock(open)
  useOverlayFocusReveal(open, panelRef)

  useEffect(() => {
    if (open) void haptic('selection')
  }, [open])

  useEffect(() => {
    if (!open) return
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [onClose, open])

  const settleSheet = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (info.offset.y > 112 || info.velocity.y > 820) {
      void haptic('soft')
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={`modal-backdrop modal-backdrop-${placement}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={iosBackdropTransition}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose()
          }}
        >
          <motion.section
            ref={panelRef}
            className={`modal-sheet glass-panel modal-${size}${className ? ` ${className}` : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={headingId}
            initial={
              reduceMotion
                ? { opacity: 0 }
                : nativeIOS
                  ? isSheet
                    ? { y: 56, opacity: 0, scale: 0.992 }
                    : { y: 10, opacity: 0, scale: 0.97 }
                  : isSheet
                    ? { y: 18, opacity: 0 }
                    : { y: 8, opacity: 0, scale: 0.98 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              reduceMotion
                ? { opacity: 0 }
                : nativeIOS
                  ? isSheet
                    ? { y: 64, opacity: 0, scale: 0.992 }
                    : { y: 8, opacity: 0, scale: 0.97 }
                  : isSheet
                    ? { y: 14, opacity: 0 }
                    : { y: 6, opacity: 0, scale: 0.98 }
            }
            transition={
              reduceMotion
                ? { duration: 0.12 }
                : nativeIOS
                  ? iosSheetSpring
                  : webSurfaceTransition
            }
            drag={supportsSheetDrag ? 'y' : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 560 }}
            dragElastic={{ top: 0.04, bottom: 0.26 }}
            dragMomentum={false}
            dragSnapToOrigin
            onDragEnd={settleSheet}
          >
            {isSheet && (
              <button
                className="modal-sheet-handle"
                type="button"
                aria-label={text('dragToClose')}
                onPointerDown={(event) => {
                  if (supportsSheetDrag) {
                    dragControls.start(event.nativeEvent, { snapToCursor: false })
                  }
                }}
              />
            )}
            <header className="modal-header">
              {headerContent ?? (
                <div>
                  {eyebrow && <span className="eyebrow">{eyebrow}</span>}
                  <h2
                    id={headingId}
                    className={hideTitle ? 'visually-hidden' : undefined}
                  >
                    {title}
                  </h2>
                </div>
              )}
              {headerContent && (
                <h2 id={headingId} className="visually-hidden">
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  className="icon-button app-close-button"
                  type="button"
                  aria-label={text('close')}
                  onClick={onClose}
                >
                  <X size={19} />
                </button>
              )}
            </header>
            {children}
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
